import axios from "../../../../services/axios";
import { useReducer, useEffect } from "react";
import { School, DbUser } from "../../../../interfaces";
import { Role } from "../../../../enums";
import { AddSingleUserProps } from "../AddDetails/AddSingleUser";
import { Class } from "@backend-types";
import { logger } from "../../../../services/logger";

export type UserCreationState = {
  endpointLoading: boolean;
  form: {
    firstName: string;
    lastName: string;
    email: string;
    schoolId: string;
    role: Role | "";
    teacherId: string;
    classId: string;
    grade: string | "";
    accessibility: boolean;
    isTester: boolean;
  };
  data: {
    schools: {
      value: School[];
      succeeded: boolean;
    };
    teachers: {
      value: DbUser[];
      succeeded: boolean;
    };
    classes: {
      value: Class[];
      succeeded: boolean;
    };
  };
  currentUser: DbUser;
};

export type UpdateFormAction = {
  type: "updateForm";
  update: Partial<UserCreationState["form"]>;
};
export type ReplaceStateAction = {
  type: "replaceState";
  state: UserCreationState;
};
export type DataSuccessAction<T extends keyof UserCreationState["data"]> = {
  type: "dataSuccess";
  key: T;
  value: UserCreationState["data"][T]["value"];
};

export type endpointLoadingAction = {
  type: "endpointLoading";
  value: any;
};

export type Action =
  | UpdateFormAction
  | ReplaceStateAction
  | DataSuccessAction<keyof UserCreationState["data"]>
  | endpointLoadingAction;

export const dataSuccess = <T extends keyof UserCreationState["data"]>({
  key,
  value,
}: Omit<DataSuccessAction<T>, "type">): DataSuccessAction<T> => ({
  type: "dataSuccess",
  key,
  value,
});

export const initUserCreationState = (
  currentUser: DbUser,
  data?: UserCreationState["data"]
): UserCreationState => {
  const state: UserCreationState = {
    endpointLoading: false,
    form: {
      firstName: "",
      lastName: "",
      email: "",
      schoolId: "",
      role: "",
      teacherId: "",
      classId: "",
      grade: "N/A",
      accessibility: false,
      isTester: false,
    },
    data: data ?? {
      schools: {
        value: [],
        succeeded: false,
      },
      teachers: {
        value: [],
        succeeded: false,
      },
      classes: {
        value: [],
        succeeded: false,
      },
    },
    currentUser,
  };
  // Admins/teachers can only add to their own school
  if ([Role.admin, Role.teacher].includes(currentUser.role)) {
    state.form.schoolId = String(currentUser.user_school[0].school_id);
    state.data.schools.value = currentUser.user_school[0].school
      ? [currentUser.user_school[0].school]
      : [];
  }
  // Teachers can only assign students to themselves
  if (currentUser.role === Role.teacher) {
    state.data.teachers.value = [currentUser];
  }

  return state;
};

function reducer(state: UserCreationState, action: Action): UserCreationState {
  switch (action.type) {
    case "endpointLoading": {
      return {
        ...state,
        endpointLoading: action.value.value,
        data: {
          ...state.data,
          [action.value.endpoint]: {
            value: [],
          },
        },
      };
    }
    case "replaceState":
      return action.state;
    case "updateForm": {
      const { update } = action;
      const nextForm = { ...state.form, ...update };
      // Supers should not have a school
      if (nextForm.role === Role.super) {
        nextForm.schoolId = "";
      }
      // Teachers can only assign students to themselves
      if (nextForm.role === Role.student && state.currentUser.role === Role.teacher) {
        nextForm.teacherId = state.currentUser.id;
      } else if (nextForm.role !== state.form.role) {
        nextForm.teacherId = "";
      }
      // Sub-entities must belong to their super-entity
      // School -> teacher -> class
      // We want to reset the sub-entity when the super-entity changes.
      if (nextForm.schoolId !== state.form.schoolId) {
        nextForm.teacherId = "";
      }
      if (nextForm.teacherId !== state.form.teacherId) {
        nextForm.classId = "";
      }
      // Only students have a grade. Empty the grade form so that users
      // are forced to choose an option instead of accidentally
      // leaving the value "N/A"
      if (state.form.role !== Role.student && nextForm.role === Role.student) {
        nextForm.grade = "";
      } else if (nextForm.role !== Role.student) {
        nextForm.grade = "N/A";
      }

      return {
        ...state,
        form: nextForm,
      };
    }
    case "dataSuccess": {
      return {
        ...state,
        data: {
          ...state.data,
          [action.key]: {
            value: action.value,
            succeeded: true,
          },
        },
      };
    }
  }
}

export function getError(state: UserCreationState): string | undefined {
  if (state.data.schools.succeeded && state.data.schools.value.length === 0) {
    return "There are no schools to select. Please contact your administrator to add the school.";
  }
  if (
    [Role.admin, Role.teacher].includes(state.currentUser.role) &&
    !state.currentUser.user_school.length
  ) {
    return "You have not been assigned to a school. Please contact your administrator to assign you to your school.";
  }
  if (
    state.form.schoolId &&
    state.form.role === Role.student &&
    state.data.teachers.succeeded &&
    state.data.teachers.value.length === 0
  ) {
    return "You do not have any associated teachers for this school. Teachers are required for class selection. Please first create a teacher user.";
  }
  if (
    state.form.schoolId &&
    state.form.teacherId &&
    state.form.role === Role.student &&
    state.data.classes.succeeded &&
    state.data.classes.value.length === 0
  ) {
    if (state.currentUser.role === Role.teacher) {
      return 'You do not have any associated classes for students to enroll.  Please first create a class in the "Add Class" form. A class is required to add a student.';
    } else {
      return 'The selected teacher does not have any associated classes for students to enroll. Please first create a class for this teacher in the "Add Class" form. A class is required to add a student.';
    }
  }
}

export function useUserCreationState(props: AddSingleUserProps) {
  const [state, dispatch] = useReducer(reducer, initUserCreationState(props.currentUser));

  useEffect(() => {
    if (!props.open) return;
    if (props.currentUser.role !== Role.super) return;
    dispatch({ type: "endpointLoading", value: { endpoint: "schools", value: true } });
    const getSchools = async () => {
      try {
        const res = await axios.get<School[]>("/schools");
        dispatch({
          type: "endpointLoading",
          value: { endpoint: "schools", value: false },
        });
        dispatch(dataSuccess({ key: "schools", value: res.data }));
      } catch (e) {
        logger.error(e);
      }
    };
    getSchools();
  }, [props.currentUser.role, props.open]);

  useEffect(() => {
    if (!props.open) return;
    // Wait to get teachers until school is chosen
    if (
      !(state.form.schoolId && [Role.super, Role.admin].includes(props.currentUser.role))
    )
      return;
    dispatch({ type: "endpointLoading", value: { endpoint: "teachers", value: true } });
    const getTeachers = async () => {
      try {
        const res = await axios.get<DbUser[]>("/teachers", {
          params: { schoolId: state.form.schoolId },
        });
        dispatch({
          type: "endpointLoading",
          value: { endpoint: "teachers", value: false },
        });
        dispatch(dataSuccess({ key: "teachers", value: res.data }));
      } catch (e) {
        logger.error(e);
      }
    };
    getTeachers();
  }, [props.currentUser.role, state.form.schoolId, props.open]);

  useEffect(() => {
    if (!props.open) return;
    // Wait to get classes until teacher is chosen
    if (!state.form.teacherId) {
      return;
    }
    dispatch({ type: "endpointLoading", value: { endpoint: "classes", value: true } });
    const getClasses = async () => {
      try {
        const res = await axios.post<Class[]>(
          "/classes",
          {},
          {
            params: { teacherId: state.form.teacherId },
          }
        );
        dispatch({
          type: "endpointLoading",
          value: { endpoint: "classes", value: false },
        });
        dispatch(dataSuccess({ key: "classes", value: res.data }));
      } catch (e) {
        logger.error(e);
      }
    };
    getClasses();
  }, [state.form.teacherId, props.open]);

  return [state, dispatch] as const;
}
